import { FC, Fragment, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import {
  ButtonApp,
  ConfirmDialog,
  FormApp,
  FormControl,
  InputApp,
  SelectField,
  SwitchField,
  TextAreaInput,
} from '@/components'
import { classNames, getVersion } from '@/utils'
import { SellTreez } from '@/models/sellTreez'
import { InstanceTypes } from '@/data'

import { backendResourcesSchema } from '../data/schema'
import { useArtifacts } from '../../groups/hooks'
import { BackendResourcesFormData } from '../data/types'
import { useUpdateSellTreez } from '../hooks'
import { sanitizeFormData } from '../utils'

const BackendResourcesForm: FC<{ sellTreez?: SellTreez }> = ({ sellTreez }) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<BackendResourcesFormData>({
    defaultValues: getBackendResourcesValuesFromSellTreez(sellTreez),
    values: getBackendResourcesValuesFromSellTreez(sellTreez),
    mode: 'onChange',
    resolver: yupResolver(backendResourcesSchema),
    disabled: !sellTreez,
  })
  const [dataToUpdate, setDataToUpdate] = useState<BackendResourcesFormData | undefined>(undefined)
  const { updateSellTreez, isUpdating } = useUpdateSellTreez(() => setDataToUpdate(undefined))

  const { artifacts, isLoading: isLoadingArtifacts } = useArtifacts()

  const lambdaReaderOptions =
    artifacts?.['selltreez-api-srv']?.map((artifact) => ({
      label: getVersion(artifact) ?? artifact,
      value: `selltreez-api-srv/${artifact}`,
    })) ?? []
  const lambdaWriterOptions =
    artifacts?.['selltreez-injection-srv']?.map((artifact) => ({
      label: getVersion(artifact) ?? artifact,
      value: `selltreez-injection-srv/${artifact}`,
    })) ?? []

  return (
    <Fragment>
      <FormApp
        handleCancel={() => {}}
        handleSubmit={handleSubmit}
        onSubmit={(data: BackendResourcesFormData) => {
          setDataToUpdate(data)
        }}
        defaultButtonsHidden
        outsideModal
      >
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <span className="text-gray-800 font-medium">Backend Resources</span>
            <ButtonApp
              type="submit"
              width="md:min-w-[160px] w-fit"
              className="h-12 flex items-center gap-2 text-sm"
              isDisabled={['REQUESTED', 'IN_PROGRESS'].includes(sellTreez?.workflowStatus ?? '')}
            >
              <span>Deploy changes</span>
            </ButtonApp>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-6 w-full">
              <fieldset className="w-full rounded-lg border border-[#F0F2F7] p-6 flex flex-col gap-6 self-start">
                <label>Lambdas</label>
                <div className="grid grid-cols-2 gap-6">
                  <FormControl label="Reader">
                    <SelectField
                      name="lambdaReader.srcKey"
                      register={register}
                      options={lambdaReaderOptions}
                      placeholder="Select version"
                      isLoading={isLoadingArtifacts}
                      control={control}
                      valueTemplate={(value) => renderItemTemplate({ value, fieldType: 'api' })}
                      itemTemplate={(value) => renderItemTemplate({ value, fieldType: 'api' })}
                      optionClassName="data-[focus]:bg-blue-50 data-[selected]:bg-blue-50 hover:bg-blue-50"
                      checkIconClassName="text-blue-100 group-data-[selected]:text-[#2E59A3]"
                    />
                  </FormControl>
                  <FormControl label="Writer">
                    <SelectField
                      name="lambdaWriter.srcKey"
                      register={register}
                      options={lambdaWriterOptions}
                      placeholder="Select version"
                      isLoading={isLoadingArtifacts}
                      control={control}
                      valueTemplate={(value) => renderItemTemplate({ value, fieldType: 'api' })}
                      itemTemplate={(value) => renderItemTemplate({ value, fieldType: 'api' })}
                      optionClassName="data-[focus]:bg-blue-50 data-[selected]:bg-blue-50 hover:bg-blue-50"
                      checkIconClassName="text-blue-100 group-data-[selected]:text-[#2E59A3]"
                    />
                  </FormControl>
                </div>
              </fieldset>
              <fieldset className="w-full rounded-lg border border-[#F0F2F7] p-6 flex flex-col gap-6 self-start">
                <label>Domain</label>
                <FormControl
                  label="Domain Name"
                  isRequired
                  messageError={errors.domain?.name?.message}
                >
                  <InputApp
                    name="domain.name"
                    placeholder="Enter domain name"
                    register={register}
                    isError={!!errors.domain?.name?.message}
                  />
                </FormControl>
                <div className="flex items-center justify-between max-w-64">
                  <label className="text-gray-400 text-sm">Enable Custom Domain</label>
                  <SwitchField name="domain.enableCustomDomain" control={control} />
                </div>
              </fieldset>
              <fieldset className="w-full rounded-lg border border-[#F0F2F7] p-6 flex flex-col gap-6 self-start">
                <label>Open Search</label>
                <FormControl
                  label="Instance Type"
                  isRequired
                  messageError={errors.opensearch?.instanceType?.message}
                >
                  <SelectField
                    name="opensearch.instanceType"
                    register={register}
                    options={InstanceTypes}
                    placeholder="Select type"
                    control={control}
                    isError={!!errors.opensearch?.instanceType?.message}
                  />
                </FormControl>
                <FormControl
                  label="Data Nodes"
                  isRequired
                  messageError={errors.opensearch?.dataNodes?.message}
                >
                  <InputApp
                    name="opensearch.dataNodes"
                    placeholder="Enter data nodes"
                    register={register}
                    type="number"
                    min={0}
                    isError={!!errors.opensearch?.dataNodes?.message}
                  />
                </FormControl>
                <FormControl
                  label="Volume Size"
                  isRequired
                  messageError={errors.opensearch?.volumeSize?.message}
                >
                  <InputApp
                    name="opensearch.volumeSize"
                    placeholder="Enter volume size"
                    register={register}
                    type="number"
                    min={0}
                    isError={!!errors.opensearch?.volumeSize?.message}
                  />
                </FormControl>
              </fieldset>
              <fieldset className="w-full rounded-lg border border-[#F0F2F7] p-6 flex flex-col gap-6 self-start">
                <label>Tracking</label>
                <FormControl
                  label="Enabled"
                  isRequired
                  messageError={errors.tracking?.enabled?.message}
                >
                  <SwitchField name="tracking.enabled" control={control} />
                </FormControl>
              </fieldset>
            </div>
            <fieldset className="rounded-lg border border-[#F0F2F7] p-6 flex flex-col gap-6 self-start">
              <label>Events</label>
              <FormControl label="Source" isRequired messageError={errors.events?.source?.message}>
                <TextAreaInput
                  rows={4}
                  name="events.source"
                  placeholder="Enter event source"
                  register={register}
                  isError={!!errors.events?.source?.message}
                />
              </FormControl>
              <FormControl
                label="Detail Type"
                isRequired
                messageError={errors.events?.detailType?.message}
              >
                <TextAreaInput
                  rows={4}
                  name="events.detailType"
                  placeholder="Enter event detail type"
                  register={register}
                  isError={!!errors.events?.detailType?.message}
                />
              </FormControl>
              <FormControl
                label="External Account ID"
                isRequired
                messageError={errors.events?.externalAccountId?.message}
              >
                <TextAreaInput
                  rows={4}
                  name="events.externalAccountId"
                  placeholder="Enter external account ID"
                  register={register}
                  isError={!!errors.events?.externalAccountId?.message}
                />
              </FormControl>
            </fieldset>
          </div>
        </div>
      </FormApp>
      <ConfirmDialog
        visible={!!dataToUpdate}
        confirmText="Are you sure yo want to deploy these changes?"
        onHide={() => setDataToUpdate(undefined)}
        onConfirm={() => updateSellTreez(sanitizeFormData(dataToUpdate!))}
        isLoading={isUpdating}
      />
    </Fragment>
  )
}

const renderItemTemplate = ({
  value,
  fieldType,
}: {
  value: string
  fieldType: 'api' | 'worker'
}) => (
  <span
    className={classNames('text-white rounded-full px-2 text-xs w-fit h-6 flex items-center', {
      'bg-[#2E59A3]': fieldType === 'api',
      'bg-[#288F58]': fieldType === 'worker',
    })}
  >
    {getVersion(value)}
  </span>
)

const getBackendResourcesValuesFromSellTreez = (
  sellTreez?: SellTreez,
): BackendResourcesFormData | undefined =>
  sellTreez
    ? {
        ...sellTreez,
        events: {
          ...sellTreez.events,
          source: JSON.stringify(sellTreez.events.source),
          detailType: JSON.stringify(sellTreez.events.detailType),
          externalAccountId: JSON.stringify(sellTreez.events.externalAccountId),
        },
      }
    : undefined

export { BackendResourcesForm }
